import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Answer } from '../Review/utils';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import RenderAnswers from 'donor/portal/pages/FeedbackDashboard/Feedback/RenderAnswers';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import {
  faTrash,
  faPen,
  faCopy,
  faCheck,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';
import { deleteReportAnswer } from './api';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { copyToClipboard } from 'shared/utils/clipboard';
import EditAnswerModal from './EditAnswer';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TrueImpactConfirmationDialog from 'shared/design-system/components/TrueImpactConfirmationDialog';
import { updateReportVersionRequest } from '../Review/api';
import { ReportBuilderData } from 'shared/providers/ReportProvider/utils';

interface AnswersListProps {
  report: ReportBuilderData;
  answers: Answer[];
  programName: string;
  isReportPassed: boolean;
  setAnswers: React.Dispatch<React.SetStateAction<Answer[]>>;
  showUpdateVersionButton?: boolean;
}

const StyledDiv = styled.div`
  .question-wrapper {
    display: flex;
    gap: ${SPACERS.small};
    padding-top: ${SPACERS.small};
    padding-left: ${SPACERS.small};
    padding-right: ${SPACERS.small};
    padding-bottom: ${SPACERS.tiny};
    &:nth-child(odd) {
      background-color: #f2f2f2;
    }
    &:nth-child(even) {
      background-color: #ffffff;
    }
    .question-id {
      width: 78px;
      display: flex;
      text-align: right;
      margin-top: ${SPACERS.tiny};
      margin-bottom: ${SPACERS.tiny};
      .question-id-text {
        flex: 1;
        cursor: pointer;
      }
      .trash-icon-wrapper {
        visibility: hidden;
        .trash-icon {
          margin-top: -7px;
        }
      }
      &:hover .trash-icon-wrapper {
        visibility: visible;
      }
    }
    .answer-wrapper {
      flex: 1;
      & * {
        font-size: 13px;
      }
    }
  }
  .update-version-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: ${SPACERS.large};
  }
  .copied-message {
    left: 50%;
    position: fixed;
    bottom: ${SPACERS.large};
    transform: translateX(-50%);
    z-index: 9999;
    padding: ${SPACERS.small} ${SPACERS.large};
    color: ${COLORS.whiteBase};
    border-radius: ${SPACERS.tiny};
    background-color: ${COLORS.brown};
    border: 3px solid ${COLORS.ctaBrandBackground};
  }
`;

const AnswersList: React.FC<AnswersListProps> = ({
  report,
  answers,
  programName,
  isReportPassed,
  setAnswers,
  showUpdateVersionButton = true,
}) => {
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest<{
    deleteNpoAnswer: { success: boolean };
  }>();
  const [
    requestUpdateVersionState,
    makeUpdateVersionRequest,
  ] = useGraphQLRequest<{
    updateReport: { success: boolean };
  }>();

  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState<
    { answer: Answer; index: number } | undefined
  >(undefined);
  const [copiedQuestionId, setCopiedQuestionId] = useState<number | undefined>(
    undefined,
  );
  const [edit, setEdit] = useState<
    { answer: Answer; index: number } | undefined
  >(undefined);

  const updateReportVersion = useCallback(() => {
    if (!client) return;
    const version = report.version === 'v4' ? null : 'v4';
    makeUpdateVersionRequest(
      updateReportVersionRequest(client, report.dbId, version),
    );
  }, [client, report, makeUpdateVersionRequest]);

  const handleCopyToClipboard = useCallback(
    (a: Answer) => {
      copyToClipboard(a.text, () => setCopiedQuestionId(a.questionId));
    },
    [setCopiedQuestionId],
  );

  useEffect(() => {
    if (!copiedQuestionId) return;
    const timer = setTimeout(() => {
      setCopiedQuestionId(undefined);
    }, 3000);
    return () => clearTimeout(timer);
  }, [copiedQuestionId]);

  const handleDelete = useCallback(
    (a: Answer, index: number) => {
      if (!client) return;
      const request = deleteReportAnswer(
        client,
        a.dbId,
        report.dbId,
        a.question.section,
      );
      makeRequest(request);
      request.then(r => {
        if (r.errors || !r.data || !r.data.deleteNpoAnswer.success) return;
        setAnswers(s => {
          const newState = [...s];
          newState.splice(index, 1);
          return newState;
        });
      });
    },
    [makeRequest, report, client, setAnswers],
  );

  return (
    <StyledDiv>
      {answers.map((a, i) => {
        const isEdit = edit?.index === i;
        return (
          <div className="question-wrapper" key={a.dbId}>
            <TrueImpactText variant="body-small-bold" className="question-id">
              <div className="trash-icon-wrapper">
                <TrueImpactIconButton
                  icon={faTrash}
                  className="trash-icon"
                  onClick={() =>
                    setDeleteConfirmationModal({ answer: a, index: i })
                  }
                />
              </div>
              <div
                onClick={() => {
                  copyToClipboard(a.questionId.toString(), () =>
                    setCopiedQuestionId(a.questionId),
                  );
                }}
                className="question-id-text"
              >
                {a.questionId}:
              </div>
            </TrueImpactText>
            <div className="answer-wrapper">
              {isEdit ? (
                <EditAnswerModal
                  index={i}
                  report={report}
                  answers={answers}
                  selectedAnswer={a}
                  programName={programName}
                  isReportPassed={isReportPassed}
                  setAnswers={setAnswers}
                  handleClose={() => setEdit(undefined)}
                />
              ) : (
                <RenderAnswers
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  answer={a as any}
                  reportId={report.dbId}
                  questionId={a.questionId}
                  expandedByDefault={false}
                  sectionId={a.question.section}
                />
              )}
            </div>
            {!isEdit && (
              <div className="edit-answer">
                <TrueImpactIconButton
                  icon={faPen}
                  label="Edit"
                  className="trash-icon"
                  onClick={() => setEdit({ answer: a, index: i })}
                />
                <TrueImpactIconButton
                  icon={faCopy}
                  label="Copy"
                  className="copy-icon"
                  onClick={() => handleCopyToClipboard(a)}
                />
              </div>
            )}
          </div>
        );
      })}
      {showUpdateVersionButton && (
        <div className="update-version-wrapper">
          <TrueImpactButton
            isLoading={requestUpdateVersionState.type === 'REQUEST_START'}
            variant="secondary-large"
            className="update-version-button"
            onClick={
              requestState.type === 'REQUEST_INIT'
                ? updateReportVersion
                : undefined
            }
          >
            {requestUpdateVersionState.type === 'REQUEST_SUCCESS' ? (
              <FontAwesomeIcon icon={faCheck} color={COLORS.success} />
            ) : requestUpdateVersionState.type === 'REQUEST_ERROR' ? (
              <FontAwesomeIcon icon={faXmark} color={COLORS.critical} />
            ) : (
              <>Mark this report as {report.version === 'v4' ? 'v3' : 'v4'}</>
            )}
          </TrueImpactButton>
        </div>
      )}
      {copiedQuestionId && (
        <TrueImpactText variant="body-bold" className="copied-message">
          {copiedQuestionId}'s answer copied to clipboard
        </TrueImpactText>
      )}
      {deleteConfirmationModal && (
        <TrueImpactConfirmationDialog
          open={true}
          iconColor="ctaPrimary"
          handleClose={() => setDeleteConfirmationModal(undefined)}
          next={{
            backgroundColor: 'critical',
            backgroundColorHover: 'criticalHover',
            text: 'Confirm Delete',
            disabled: false,
            onClick: () => {
              handleDelete(
                deleteConfirmationModal.answer,
                deleteConfirmationModal.index,
              );
              setDeleteConfirmationModal(undefined);
            },
          }}
          previous={{
            text: 'Cancel',
            onClick: () => setDeleteConfirmationModal(undefined),
          }}
        >
          <TrueImpactText>
            <div style={{ textAlign: 'center' }}>
              Are you sure you want to delete{' '}
              <strong>{deleteConfirmationModal.answer.questionId}</strong>?
            </div>
            <br />
            <RenderAnswers
              reportId={report.dbId}
              expandedByDefault={false}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              answer={deleteConfirmationModal.answer as any}
              questionId={deleteConfirmationModal.answer.questionId}
              sectionId={deleteConfirmationModal.answer.question.section}
            />
          </TrueImpactText>
        </TrueImpactConfirmationDialog>
      )}
    </StyledDiv>
  );
};

export default AnswersList;
